import { z } from "zod";
import { dateRangeSchema } from "./accounting.js";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
import { fileSchema } from "./files.js";
import { stockQtySchema } from "./inventory.js";
import { paisa } from "./money.js";
import { orderChannelSchema } from "./orders.js";
/** Summary tables, the dashboard, reports and their exports (PRD §11.3; docs/specs/reporting.md). */
// Summaries (B-55) ---------------------------------------------------------------------------------------------------
export const rebuildSummariesRequestSchema = z
    .object({ from: z.iso.date(), to: z.iso.date() })
    .strict()
    .and(dateRangeSchema);
export const rebuildSummariesResponseSchema = z.object({
    from: z.iso.date(),
    to: z.iso.date(),
    days: z.int(),
});
const tag = { tag: "reporting" };
export const rebuildSummaries = defineEndpoint({
    ...tag,
    name: "rebuildSummaries",
    method: "POST",
    path: "/v1/reports/summaries/rebuild",
    access: { permission: "reports.rebuild" },
    summary: "Recompute the daily sales and product summaries of a period from the orders (runs as a job)",
    body: rebuildSummariesRequestSchema,
    status: 202,
    response: rebuildSummariesResponseSchema,
});
// Dashboard (B-56) ---------------------------------------------------------------------------------------------------
export const dashboardQuerySchema = z
    .object({ from: z.iso.date(), to: z.iso.date(), locationId: z.uuid().optional() })
    .and(dateRangeSchema);
const salesFigures = z.object({
    /** Total + rounding − VAT. */
    netSalesPaisa: paisa(),
    ordersCount: z.int(),
    /** Net sales ÷ orders (half-up); 0 without orders. */
    averageOrderPaisa: paisa(),
    discountsPaisa: paisa(),
    taxPaisa: paisa(),
    /** Returned goods without their VAT. */
    returnsPaisa: paisa(),
    /** Net sales − returns − COGS + returned COGS. */
    grossProfitPaisa: paisa(),
});
export const dashboardSchema = z.object({
    from: z.iso.date(),
    to: z.iso.date(),
    /** The same number of days just before `from`. */
    previousFrom: z.iso.date(),
    previousTo: z.iso.date(),
    sales: salesFigures,
    previous: salesFigures,
    series: z.array(z.object({ date: z.iso.date(), netSalesPaisa: paisa(), ordersCount: z.int(), grossProfitPaisa: paisa() })),
    channels: z.array(z.object({ channel: orderChannelSchema, netSalesPaisa: paisa(), ordersCount: z.int() })),
    topProducts: z.array(z.object({
        variantId: z.uuid(),
        name: z.string(),
        sku: z.string(),
        qtySold: stockQtySchema,
        unitPrecision: z.int(),
        salesPaisa: paisa(),
    })),
    /** Null without `inventory.view`. */
    stock: z
        .object({
        negative: z.int(),
        low: z.int(),
        items: z.array(z.object({
            variantId: z.uuid(),
            name: z.string(),
            locationId: z.uuid(),
            onHand: stockQtySchema,
            reorderPoint: stockQtySchema.nullable(),
            unitPrecision: z.int(),
        })),
    })
        .nullable(),
    /** Null without `orders.view`. */
    orders: z.object({ pending: z.int(), toShip: z.int() }).nullable(),
    /** Null without `cod.view`: money couriers collected and have not paid out yet. */
    cod: z.object({ heldPaisa: paisa(), parcels: z.int() }).nullable(),
});
export const getDashboard = defineEndpoint({
    ...tag,
    name: "getDashboard",
    method: "GET",
    path: "/v1/dashboard",
    access: { permission: "dashboard.view" },
    summary: "Sales, profit, channels and top products of a period (from the summaries), with what needs action now",
    query: dashboardQuerySchema,
    status: 200,
    response: dashboardSchema,
});
// Reports (B-57) -----------------------------------------------------------------------------------------------------
/** Reports by group; each group is one route guarded by `reports.<group>` (docs/specs/reporting.md). */
export const REPORT_GROUPS = {
    sales: ["sales_summary", "product_profit", "orders", "returns"],
    stock: ["stock_valuation", "stock_movements", "dead_stock", "low_stock"],
    finance: ["profit_loss", "vat", "receivables", "payables", "expenses", "transactions"],
    pos: ["shifts", "price_variances", "review_queue"],
    delivery: ["delivery_performance"],
    customers: ["customers"],
};
/**
 * PL-07 (docs/specs/plan-catalogue.md): report groups that need the plan's `feature.advanced_reports` — profit, stock
 * value and customers. Every export needs it too; sales, POS and delivery reports on screen never do.
 */
export const ADVANCED_REPORT_GROUPS = ["finance", "stock", "customers"];
export const REPORT_GROUP_KEYS = Object.keys(REPORT_GROUPS);
export const reportGroupSchema = z.enum(REPORT_GROUP_KEYS);
export const SALES_GROUP_BY = [
    "day",
    "hour",
    "product",
    "category",
    "brand",
    "staff",
    "channel",
    "payment_method",
    "location",
];
/** Rows a report shows on screen, and the most an export writes. */
export const REPORT_SCREEN_ROWS = 1_000;
export const REPORT_EXPORT_ROWS = 50_000;
/** The common filters (PRD §11.3: date, location, channel, staff) and a few report-specific ones. */
export const reportQuerySchema = z
    .object({
    from: z.iso.date(),
    to: z.iso.date(),
    locationId: z.uuid().optional(),
    channel: orderChannelSchema.optional(),
    staffId: z.uuid().optional(),
    /** `sales_summary`: how rows are grouped (default `day`). */
    groupBy: z.enum(SALES_GROUP_BY).optional(),
    /** `dead_stock`: unsold for at least this many days (default 60). */
    days: z.coerce.number().int().min(1).max(3650).optional(),
    /** `transactions`: one kind of money movement. */
    kind: z.string().trim().min(1).max(40).optional(),
    /** `expenses`: one category. */
    categoryId: z.uuid().optional(),
    /** `stock_movements`: one variant. */
    variantId: z.uuid().optional(),
    /** `review_queue`: `open` (default) or `resolved`. */
    status: z.enum(["open", "resolved"]).optional(),
})
    .and(dateRangeSchema);
export const REPORT_COLUMN_TYPES = ["text", "date", "datetime", "money", "qty", "count", "percent"];
export const reportColumnSchema = z.object({
    key: z.string(),
    /** money: paisa; qty: a decimal string; percent: basis points; date / datetime: ISO. */
    type: z.enum(REPORT_COLUMN_TYPES),
});
export const reportCellSchema = z.union([z.string(), z.number(), z.null()]);
/** Where a row leads: a narrower report, or the record behind it. */
export const reportDrillSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("report"),
        group: reportGroupSchema,
        report: z.string(),
        query: z.record(z.string(), z.string()),
    }),
    z.object({ kind: z.enum(["order", "product", "customer", "supplier", "expense"]), id: z.uuid() }),
]);
export const reportResultSchema = z.object({
    group: reportGroupSchema,
    report: z.string(),
    columns: z.array(reportColumnSchema),
    rows: z.array(z.object({ cells: z.record(z.string(), reportCellSchema), drill: reportDrillSchema.nullable() })),
    /** Sums of the numeric columns that add up (null when nothing sums). */
    totals: z.record(z.string(), reportCellSchema).nullable(),
    /** More rows exist than were returned. */
    truncated: z.boolean(),
});
const reportEndpoint = (group, name, summary) => defineEndpoint({
    ...tag,
    name,
    method: "GET",
    path: `/v1/reports/${group}/{report}`,
    access: { permission: `reports.${group}` },
    summary,
    params: z.object({ report: z.enum(REPORT_GROUPS[group]) }),
    query: reportQuerySchema,
    status: 200,
    response: reportResultSchema,
});
export const getSalesReport = reportEndpoint("sales", "getSalesReport", "Sales summary, product profit, orders, returns");
export const getStockReport = reportEndpoint("stock", "getStockReport", "Stock valuation, movements, dead and low stock");
export const getFinanceReport = reportEndpoint("finance", "getFinanceReport", "Profit and loss, VAT, receivables, payables, expenses, money movements");
export const getPosReport = reportEndpoint("pos", "getPosReport", "Shifts and Z-reports, price variances, review queue");
export const getDeliveryReport = reportEndpoint("delivery", "getDeliveryReport", "Delivered vs returned by courier and area");
export const getCustomersReport = reportEndpoint("customers", "getCustomersReport", "Top customers, dues, repeat rate");
/** The report endpoint of each group (for clients that pick the group at run time). */
export const REPORT_ENDPOINTS = {
    sales: getSalesReport,
    stock: getStockReport,
    finance: getFinanceReport,
    pos: getPosReport,
    delivery: getDeliveryReport,
    customers: getCustomersReport,
};
// Exports (B-58) -----------------------------------------------------------------------------------------------------
export const REPORT_EXPORT_FORMATS = ["xlsx", "csv", "pdf"];
export const startReportExportRequestSchema = z
    .object({
    format: z.enum(REPORT_EXPORT_FORMATS),
    query: reportQuerySchema,
    /** The language of the file's headings and values. */
    locale: z.enum(["en", "bn"]).default("en"),
})
    .strict();
export const reportExportSchema = z.object({
    id: z.uuid(),
    group: reportGroupSchema,
    report: z.string(),
    format: z.enum(REPORT_EXPORT_FORMATS),
    status: z.enum(["queued", "running", "done", "failed"]),
    rowCount: z.int().nullable(),
    /** The file once done; its `urls.original` is a short-lived signed link. */
    file: fileSchema.nullable(),
    error: z.string().nullable(),
    createdAt: z.iso.datetime(),
    finishedAt: z.iso.datetime().nullable(),
});
const exportEndpoint = (group, name) => defineEndpoint({
    ...tag,
    name,
    method: "POST",
    path: `/v1/reports/${group}/{report}/exports`,
    access: { permission: `reports.${group}` },
    summary: "Write the report to an Excel, CSV or PDF file in the background; you are notified when it is ready",
    params: z.object({ report: z.enum(REPORT_GROUPS[group]) }),
    body: startReportExportRequestSchema,
    status: 202,
    response: reportExportSchema,
});
export const exportSalesReport = exportEndpoint("sales", "exportSalesReport");
export const exportStockReport = exportEndpoint("stock", "exportStockReport");
export const exportFinanceReport = exportEndpoint("finance", "exportFinanceReport");
export const exportPosReport = exportEndpoint("pos", "exportPosReport");
export const exportDeliveryReport = exportEndpoint("delivery", "exportDeliveryReport");
export const exportCustomersReport = exportEndpoint("customers", "exportCustomersReport");
export const REPORT_EXPORT_ENDPOINTS = {
    sales: exportSalesReport,
    stock: exportStockReport,
    finance: exportFinanceReport,
    pos: exportPosReport,
    delivery: exportDeliveryReport,
    customers: exportCustomersReport,
};
export const getReportExport = defineEndpoint({
    ...tag,
    name: "getReportExport",
    method: "GET",
    path: "/v1/report-exports/{id}",
    access: { signedIn: true },
    summary: "One of your report exports: its status and, once done, the file",
    params: idParamsSchema,
    status: 200,
    response: reportExportSchema,
});
//# sourceMappingURL=reports.js.map